/**
 * TypeDropdown - Filtro multi-select por tipología de infraestructura
 * 
 * Dropdown en header de tabla para filtrar por tipos (checkboxes).
 * Colores Material Design por tipología.
 * 
 * @source P1_Normaliza_v7_fixed.html
 * @version 2.0.0 - Multi-select con checkboxes
 */

import React, { useState, useRef, useEffect, type FC, type CSSProperties } from 'react';
import {
  colors,
  typography,
  borderRadius,
  shadows,
  transitions,
} from '@/design-system/v2';

// ============================================================================
// TYPES
// ============================================================================

export type TypologyType = 
  | 'sanitario' 
  | 'educativo' 
  | 'policial' 
  | 'cultural' 
  | 'religioso' 
  | 'emergencias' 
  | 'deportivo'
  | 'administrativo'
  | 'infraestructura';

/** @deprecated Use TypologyType[] para multi-select */
export type TypologyFilter = 'all' | TypologyType;

export interface TypeDropdownProps {
  /** Tipos seleccionados (array vacío = todos) */
  selected?: TypologyType[];
  /** Callback al cambiar selección */
  onSelectionChange?: (types: TypologyType[]) => void;
  /** Si true, centra el menú desplegable (default: true) */
  centered?: boolean;
  // Legacy support
  value?: TypologyFilter;
  onChange?: (type: TypologyFilter) => void;
}

// ============================================================================
// TYPE CONFIG
// ============================================================================

const TYPE_OPTIONS: { type: TypologyType; label: string }[] = [
  { type: 'sanitario', label: 'Sanitario' },
  { type: 'educativo', label: 'Educativo' },
  { type: 'policial', label: 'Seguridad' },
  { type: 'cultural', label: 'Cultural' },
  { type: 'religioso', label: 'Religioso' },
  { type: 'emergencias', label: 'Emergencias' },
  { type: 'deportivo', label: 'Deportivo' },
  { type: 'administrativo', label: 'Administrativo' },
  { type: 'infraestructura', label: 'Infraestructura' },
];

const ALL_TYPES = TYPE_OPTIONS.map(o => o.type);

// ============================================================================
// TYPOLOGY MAPPING — nombre largo → código DS v2
// ============================================================================

const TYPOLOGY_CODE_MAP: Record<TypologyType, keyof typeof colors.typologies> = {
  sanitario: 'san',
  educativo: 'edu',
  policial: 'pol',
  cultural: 'cul',
  religioso: 'rel',
  emergencias: 'eme',
  deportivo: 'dep',
  administrativo: 'adm',
  infraestructura: 'inf',
};

// ============================================================================
// STYLES
// ============================================================================

const STYLES = {
  container: {
    position: 'relative' as const,
    display: 'inline-flex',
  } as CSSProperties,

  button: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    background: 'transparent',
    border: 'none',
    color: 'inherit',
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.semibold,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: borderRadius.sm,
    transition: transitions.base,
    fontFamily: typography.fontFamily.sans,
  } as CSSProperties,

  buttonHover: {
    background: colors.bg.secondary,
    color: colors.fg.primary,
  } as CSSProperties,

  chevron: {
    width: 10,
    height: 10,
    opacity: 0.6,
  } as CSSProperties,

  chevronHover: {
    opacity: 1,
  } as CSSProperties,

  menu: (centered: boolean): CSSProperties => ({
    position: 'absolute' as const,
    top: 'calc(100% + 4px)',
    left: centered ? '50%' : '0',
    transform: centered ? 'translateX(-50%)' : 'none',
    background: colors.bg.secondary,
    border: `1px solid ${colors.border.DEFAULT}`,
    borderRadius: borderRadius.lg,
    minWidth: 180,
    padding: '6px',
    zIndex: 50,
    boxShadow: shadows.lg,
  }),

  menuHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '4px 8px 8px',
    borderBottom: `1px solid ${colors.border.light}`,
    marginBottom: '4px',
  } as CSSProperties,

  menuHeaderButton: {
    fontSize: '10px',
    color: colors.primary,
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '2px 6px',
    borderRadius: borderRadius.sm,
    fontFamily: typography.fontFamily.sans,
    transition: transitions.base,
  } as CSSProperties,

  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 10px',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.normal,
    color: colors.fg.secondary,
    borderRadius: borderRadius.sm,
    cursor: 'pointer',
    transition: transitions.base,
    background: 'transparent',
    border: 'none',
    width: '100%',
    textAlign: 'left' as const,
    fontFamily: typography.fontFamily.sans,
  } as CSSProperties,

  menuItemHover: {
    background: colors.bg.tertiary,
    color: colors.fg.primary,
  } as CSSProperties,

  checkbox: {
    width: 14,
    height: 14,
    borderRadius: '3px',
    border: `1.5px solid ${colors.border.DEFAULT}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: transitions.base,
  } as CSSProperties,

  checkboxChecked: {
    background: 'transparent', // Solo check, sin fondo
    borderColor: colors.primary, // Borde teal al checkear
  } as CSSProperties,

  typeDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  } as CSSProperties,

  labelText: {
    flex: 1,
  } as CSSProperties,
};

// ============================================================================
// ICONS
// ============================================================================

const ChevronDown: FC<{ hovered?: boolean }> = ({ hovered }) => (
  <svg
    viewBox="0 0 24 24"
    style={{ ...STYLES.chevron, ...(hovered ? STYLES.chevronHover : {}) }}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CheckIcon: FC = () => (
  <svg viewBox="0 0 24 24" width={10} height={10} fill="none" stroke={colors.primary} strokeWidth={3}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// ============================================================================
// HELPERS
// ============================================================================

function getTypeColor(type: TypologyType): string {
  const code = TYPOLOGY_CODE_MAP[type];
  return code ? colors.typologies[code].color : colors.fg.secondary;
}

function getButtonLabel(selected: TypologyType[]): string {
  if (selected.length === 0 || selected.length === ALL_TYPES.length) {
    return 'Tipología';
  }
  if (selected.length === 1) {
    const opt = TYPE_OPTIONS.find(o => o.type === selected[0]);
    return opt?.label || 'Tipología';
  }
  return `${selected.length} tipos`;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const TypeDropdown: FC<TypeDropdownProps> = ({
  selected: selectedProp,
  onSelectionChange,
  centered = true,
  // Legacy props
  value,
  onChange,
}) => {
  // Support legacy single-select API
  const isLegacyMode = value !== undefined && onChange !== undefined;
  
  const [internalSelected, setInternalSelected] = useState<TypologyType[]>([]);
  
  // Derive selected from legacy or new API
  const selected = isLegacyMode
    ? (value === 'all' ? [] : [value as TypologyType])
    : (selectedProp ?? internalSelected);

  const [isOpen, setIsOpen] = useState(false);
  const [buttonHovered, setButtonHovered] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<TypologyType | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Cerrar al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleToggle = (type: TypologyType) => {
    const isSelected = selected.includes(type);
    let newSelected: TypologyType[];
    
    if (isSelected) {
      newSelected = selected.filter(t => t !== type);
    } else {
      newSelected = [...selected, type];
    }
    
    // If all selected, treat as "all" (empty array)
    if (newSelected.length === ALL_TYPES.length) {
      newSelected = [];
    }
    
    if (isLegacyMode && onChange) {
      // Legacy: convert to single value
      onChange(newSelected.length === 0 ? 'all' : newSelected[0]);
    } else {
      setInternalSelected(newSelected);
      onSelectionChange?.(newSelected);
    }
  };

  const handleSelectAll = () => {
    if (isLegacyMode && onChange) {
      onChange('all');
    } else {
      setInternalSelected([]);
      onSelectionChange?.([]);
    }
  };

  const handleSelectNone = () => {
    // Select none = show nothing (but we'll treat empty as "all" for filtering)
    // So "ninguno" really means deselect all currently selected
    if (isLegacyMode && onChange) {
      onChange('all');
    } else {
      setInternalSelected([]);
      onSelectionChange?.([]);
    }
  };

  const isChecked = (type: TypologyType): boolean => {
    // Empty array means all selected
    if (selected.length === 0) return true;
    return selected.includes(type);
  };

  const allSelected = selected.length === 0;

  const buttonStyle: CSSProperties = {
    ...STYLES.button,
    ...(buttonHovered ? STYLES.buttonHover : {}),
  };

  const getItemStyle = (type: TypologyType): CSSProperties => {
    const isHovered = hoveredItem === type;
    return {
      ...STYLES.menuItem,
      ...(isHovered ? STYLES.menuItemHover : {}),
    };
  };

  const getCheckboxStyle = (type: TypologyType): CSSProperties => {
    const checked = isChecked(type);
    return {
      ...STYLES.checkbox,
      ...(checked ? STYLES.checkboxChecked : {}),
    };
  };

  return (
    <div style={STYLES.container} ref={containerRef}>
      <button
        style={buttonStyle}
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setButtonHovered(true)}
        onMouseLeave={() => setButtonHovered(false)}
      >
        {getButtonLabel(selected)}
        <ChevronDown hovered={buttonHovered} />
      </button>

      {isOpen && (
        <div style={STYLES.menu(centered)}>
          {/* Header con Todos/Ninguno */}
          <div style={STYLES.menuHeader}>
            <button
              style={{
                ...STYLES.menuHeaderButton,
                opacity: allSelected ? 0.5 : 1,
              }}
              onClick={handleSelectAll}
            >
              Todos
            </button>
            <button
              style={{
                ...STYLES.menuHeaderButton,
                opacity: !allSelected && selected.length === 0 ? 0.5 : 1,
              }}
              onClick={handleSelectNone}
            >
              Ninguno
            </button>
          </div>

          {/* Lista de tipos con checkboxes */}
          {TYPE_OPTIONS.map((option) => (
            <button
              key={option.type}
              style={getItemStyle(option.type)}
              onClick={() => handleToggle(option.type)}
              onMouseEnter={() => setHoveredItem(option.type)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <span style={getCheckboxStyle(option.type)}>
                {isChecked(option.type) && <CheckIcon />}
              </span>
              <span 
                style={{ 
                  ...STYLES.typeDot, 
                  background: getTypeColor(option.type) 
                }} 
              />
              <span style={STYLES.labelText}>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TypeDropdown;
